import React, { useState, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useAdmitCardSubjects } from '../hooks/useAdmitCardSubjects';

// Course subject structure from students list API
interface CourseSubject {
  _id: string;
  name: string;
  code: string;
  year?: number | null;
  semester?: number | null;
  credits?: number | null;
  courseId?: string;
  courseName?: string;
  createdAt?: string;
  updatedAt?: string;
}

// Mapped subject for dialog display
interface SubjectForDisplay {
  _id: string;
  subjectCode: string;
  subjectName: string;
  year?: string;
  semester?: string;
  examDate?: string;
}

interface ApproveAdmitCardDialogProps {
  open: boolean;
  onClose: () => void;
  onApprove: (year?: string, semester?: string) => void;
  registrationNo: string;
  studentName: string;
  studentId: string;
  isLoading?: boolean;
  approvedYears?: string[];
  approvedSemesters?: string[];
  subjects?: CourseSubject[];
}

const ApproveAdmitCardDialog: React.FC<ApproveAdmitCardDialogProps> = ({
  open,
  onClose,
  onApprove,
  registrationNo,
  studentName,
  studentId,
  isLoading = false,
  approvedYears = [],
  approvedSemesters = [],
  subjects: propsSubjects = [],
}) => {
  const [approvalType, setApprovalType] = useState<'year' | 'semester'>('year');
  const [selectedTerm, setSelectedTerm] = useState<string>('');

  // Fetch admit card subjects if not provided in props (fallback)
  const { data: admitCardSubjectsData = [], isLoading: isLoadingAdmitCardSubjects } = useAdmitCardSubjects(
    propsSubjects.length === 0 ? studentId : ''
  );

  // Use props subjects if available, otherwise use fetched admit card subjects
  const subjectsToUse = useMemo(() => {
    if (propsSubjects.length > 0) {
      return propsSubjects;
    }
    // Map admit card subjects to course subject format
    return admitCardSubjectsData.map(sub => ({
      _id: sub._id || '',
      name: sub.subjectName || '',
      code: sub.subjectCode || '',
      year: sub.year ? parseInt(sub.year) : null,
      semester: sub.semester ? parseInt(sub.semester) : null,
      credits: null,
      courseId: '',
      courseName: '',
    }));
  }, [propsSubjects, admitCardSubjectsData]);

  // Map course subjects to display format
  const mappedSubjects = useMemo<SubjectForDisplay[]>(() => {
    return subjectsToUse.map(sub => ({
      _id: sub._id,
      subjectCode: sub.code || '',
      subjectName: sub.name || '',
      year: sub.year !== null && sub.year !== undefined ? String(sub.year) : undefined,
      semester: sub.semester !== null && sub.semester !== undefined ? String(sub.semester) : undefined,
      examDate: '', // Course subjects don't have exam dates
    }));
  }, [subjectsToUse]);

  // Determine if course is year-based or semester-based from subjects
  const isYearBased = useMemo(() => {
    if (mappedSubjects.length === 0) return false;
    // Check if any subject has a year (and not semester)
    const hasYear = mappedSubjects.some(sub => sub.year && !sub.semester);
    const hasSemester = mappedSubjects.some(sub => sub.semester && !sub.year);
    
    // If we have both, prefer year-based if more subjects have years
    if (hasYear && hasSemester) {
      const yearCount = mappedSubjects.filter(sub => sub.year && !sub.semester).length;
      const semCount = mappedSubjects.filter(sub => sub.semester && !sub.year).length;
      return yearCount >= semCount;
    }
    
    return hasYear;
  }, [mappedSubjects]);

  // Set default approval type based on course structure
  React.useEffect(() => {
    if (isYearBased) {
      setApprovalType('year');
    } else {
      setApprovalType('semester');
    }
  }, [isYearBased]);

  // Get available years/semesters from subjects
  const availableTerms = useMemo(() => {
    if (approvalType === 'year') {
      const years = [...new Set(mappedSubjects.filter(sub => sub.year).map(sub => sub.year!))];
      return years.sort((a, b) => parseInt(a) - parseInt(b));
    } else {
      const semesters = [...new Set(mappedSubjects.filter(sub => sub.semester).map(sub => sub.semester!))];
      return semesters.sort((a, b) => parseInt(a) - parseInt(b));
    }
  }, [mappedSubjects, approvalType]);

  // Get approved terms from props
  const approvedTerms = useMemo(() => {
    return approvalType === 'year' ? approvedYears : approvedSemesters;
  }, [approvalType, approvedYears, approvedSemesters]);

  // Get unapproved terms
  const unapprovedTerms = useMemo(() => {
    return availableTerms.filter(term => !approvedTerms.includes(term));
  }, [availableTerms, approvedTerms]);

  // Get subjects for selected term
  const subjectsForSelectedTerm = useMemo(() => {
    if (!selectedTerm) return [];
    return mappedSubjects.filter(sub => {
      if (approvalType === 'year') {
        return sub.year === selectedTerm;
      } else {
        return sub.semester === selectedTerm;
      }
    });
  }, [mappedSubjects, selectedTerm, approvalType]);

  // Reset selected term when approval type changes
  React.useEffect(() => {
    setSelectedTerm('');
  }, [approvalType]);

  // Auto-select first unapproved term if available
  React.useEffect(() => {
    if (unapprovedTerms.length > 0 && !selectedTerm) {
      setSelectedTerm(unapprovedTerms[0]);
    }
  }, [unapprovedTerms, selectedTerm]);

  const handleApprove = () => {
    if (!selectedTerm) return;
    if (approvalType === 'year') {
      onApprove(selectedTerm, undefined);
    } else {
      onApprove(undefined, selectedTerm);
    }
  };

  const handleClose = () => {
    setSelectedTerm('');
    setApprovalType(isYearBased ? 'year' : 'semester');
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>
              Approve Admit Card
            </Typography>
            <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
              {studentName} ({registrationNo})
            </Typography>
          </Box>
          <IconButton
            onClick={handleClose}
            sx={{
              color: '#64748b',
              '&:hover': { backgroundColor: '#f1f5f9' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {isLoadingAdmitCardSubjects && propsSubjects.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
            <CircularProgress size={40} />
          </Box>
        ) : mappedSubjects.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No subjects found for this student. Please add subjects first.
          </Alert>
        ) : (
          <>
            {/* Approval Type Selection */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#1e293b' }}>
                Select Approval Type
              </Typography>
              <RadioGroup
                row
                value={approvalType}
                onChange={(e) => setApprovalType(e.target.value as 'year' | 'semester')}
              >
                <FormControlLabel
                  value="year"
                  control={<Radio />}
                  label="Approve by Year"
                  disabled={!isYearBased && availableTerms.length === 0}
                />
                <FormControlLabel
                  value="semester"
                  control={<Radio />}
                  label="Approve by Semester"
                  disabled={isYearBased && availableTerms.length === 0}
                />
              </RadioGroup>
            </Box>

            {/* Term Selection */}
            {availableTerms.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <FormControl fullWidth>
                  <InputLabel>Select {approvalType === 'year' ? 'Year' : 'Semester'}</InputLabel>
                  <Select
                    value={selectedTerm}
                    label={`Select ${approvalType === 'year' ? 'Year' : 'Semester'}`}
                    onChange={(e) => setSelectedTerm(e.target.value)}
                  >
                    {availableTerms.map((term) => (
                      <MenuItem key={term} value={term}>
                        {approvalType === 'year' ? 'Year' : 'Semester'} {term}
                        {approvedTerms.includes(term) && ' (Already Approved)'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            )}

            {/* Subjects Preview */}
            {selectedTerm && subjectsForSelectedTerm.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#1e293b' }}>
                  Subjects for {approvalType === 'year' ? 'Year' : 'Semester'} {selectedTerm}
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Subject Code</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Subject Name</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>Exam Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {subjectsForSelectedTerm.map((subject) => (
                        <TableRow key={subject._id}>
                          <TableCell>{subject.subjectCode || 'N/A'}</TableCell>
                          <TableCell>{subject.subjectName}</TableCell>
                          <TableCell>{subject.examDate || 'TBA'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {availableTerms.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No {approvalType === 'year' ? 'years' : 'semesters'} found with subjects. Please add subjects first.
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 2, gap: 2 }}>
        <Button
          onClick={handleClose}
          disabled={isLoading}
          sx={{
            color: '#64748b',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#f1f5f9',
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleApprove}
          disabled={isLoading || !selectedTerm || subjectsForSelectedTerm.length === 0}
          variant="contained"
          sx={{
            backgroundColor: '#6366f1',
            color: 'white',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            py: 1,
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#4f46e5',
            },
            '&:disabled': {
              backgroundColor: '#9ca3af',
              color: 'white',
            },
          }}
        >
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} color="inherit" />
              Approving...
            </Box>
          ) : (
            `Approve ${approvalType === 'year' ? 'Year' : 'Semester'} ${selectedTerm || ''}`
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveAdmitCardDialog;

